import { Languages } from "lucide-react"

import { LOCALES, type Locale, localeOfPath, localizePath, stripLocale } from "@/core/i18n/locale"
import { ZH_PAGE_PATHS } from "@/core/seo/prerender"

/**
 * Where the switcher goes from `pathname`: the same page in the other language when it exists,
 * otherwise the Chinese home page (from English) or the English page (from Chinese, always there).
 * No automatic redirect by IP or browser language anywhere (seo-spec section 3a).
 */
export function languageSwitchTarget(pathname: string): { locale: Locale; href: string } {
  const current = localeOfPath(pathname)
  const english = stripLocale(pathname).replace(/(.)\/$/, "$1")
  if (current === "zh") return { locale: "en", href: localizePath(english, "en") }
  return {
    locale: "zh",
    href: ZH_PAGE_PATHS.includes(english) ? localizePath(english, "zh") : localizePath("/", "zh"),
  }
}

const linkClass =
  "text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 font-medium outline-none focus-visible:ring-2"

/** Plain link to the other language (works before hydration; `lang` and `hrefLang` name its language). */
export const LanguageSwitcher = ({ pathname }: { pathname: string }) => {
  const { locale, href } = languageSwitchTarget(pathname)
  const info = LOCALES[locale]
  return (
    <a href={href} hrefLang={info.hreflang} lang={info.htmlLang} className={linkClass}>
      <Languages className="size-4" aria-hidden="true" />
      {info.label}
    </a>
  )
}
